import { getStoredUser } from '../utils/safeStorage';
import { ROLE_PERMISSIONS, hasPermission as checkPermission, hasModuleAccess } from '../constants/permissions';

/**
 * Hook para obter permissoes do usuario logado
 * Suporta tanto as permissoes antigas (string-based) quanto as novas (RBAC granular)
 */
const useGetUserPermission = () => {
    const user = getStoredUser();
    const role = user?.role;

    if (!role) return [];

    // Permissoes RBAC granulares
    const rbacPermissions = ROLE_PERMISSIONS[role] || [];

    // Backward compat: mapear permissoes antigas para manter compatibilidade
    // com componentes que ainda usam os nomes antigos
    const legacyPermissions = [];

    if (role === 'Admin') {
        legacyPermissions.push(
            'default', 'viewUser', 'viewRoleAdmin',
            'viewIndustry', 'viewRoleEmpresa', 'AdminOnly'
        );
    }

    if (['empresa', 'owner', 'manager'].includes(role)) {
        legacyPermissions.push(
            'default', 'viewUser', 'viewRoleRevenda', 'viewRevenda'
        );
    }

    if (['caixa'].includes(role)) {
        legacyPermissions.push('default');
    }

    if (['cozinha'].includes(role)) {
        legacyPermissions.push('default');
    }

    if (['entregador'].includes(role)) {
        legacyPermissions.push('default');
    }

    return [...new Set([...legacyPermissions, ...rbacPermissions])];
};

/**
 * Hook para verificar uma permissao RBAC especifica
 * @param {string} permission - Ex: 'pedidos:read', 'empresa:billing'
 * @returns {boolean}
 */
export const useHasPermission = (permission) => {
    const user = getStoredUser();
    if (!user?.role) return false;
    return checkPermission(user.role, permission);
};

/**
 * Hook para verificar acesso a um modulo especifico (RBAC modular)
 * @param {string} moduleName - Ex: 'pedidos', 'caixa', 'cardapio'
 * @param {string} level - 'read' | 'write' | 'delete' (default: 'read')
 * @returns {boolean}
 */
export const useHasModule = (moduleName, level = 'read') => {
    const user = getStoredUser();
    if (!user) return false;
    return hasModuleAccess(user, moduleName, level);
};

/**
 * Componente gate que renderiza children apenas se usuario tem TODAS as permissoes listadas
 * Suporta tanto permissoes antigas quanto RBAC
 */
const PermissionGate = ({ children, permissions }) => {
    const userPermissions = useGetUserPermission();

    // Se usuario tem wildcard (*), tem acesso a tudo EXCETO AdminOnly
    // AdminOnly é exclusivo para role Admin (administradores da plataforma)
    if (userPermissions.includes('*')) {
        const requiresAdminOnly = permissions.includes('AdminOnly');
        if (requiresAdminOnly && !userPermissions.includes('AdminOnly')) {
            return null;
        }
        return children;
    }

    if (
        permissions.every(permission => {
            return userPermissions.includes(permission);
        })
    ) {
        return children;
    }

    return null;
};

/**
 * Componente gate que renderiza children se usuario tem a permissao RBAC
 * Mais simples que PermissionGate — aceita uma unica permission string
 */
export const RbacGate = ({ children, permission }) => {
    const allowed = useHasPermission(permission);
    return allowed ? children : null;
};

export default PermissionGate;
